import React, { useMemo } from 'react';
import { TextTruncate } from '@this/shared/ui/data_displays/typography';
import { type AITTheme } from '@this/shared/ui/theme';
import { getCircleNumber } from '@this/src/util/string/circledNumberMarks';
import { formatPriceWithMark } from '@this/src/util/price/price.util';
import {
  PROPOSAL_BUNDLE_CATEGORY,
  TICKET_CATEGORY,
  TICKET_CATEGORY_LABELS,
  type ProposalBundle,
  type TransportProposalItem,
  type TicketCategory
} from '../../../types/proposalBundles';
import { TransportTimeRangeCell } from '../../TransportTimeRangeCell';
import { buildForeignFlightRowDisplay } from '../utils';

const SEGMENT_TITLE_MIN_LEN = 52;

const resolveChangeability = (item: TransportProposalItem): TicketCategory | null => {
  const markers = [item.transport.ticketCategory, item.transport.ticketType];
  if (markers.includes(TICKET_CATEGORY.CHANGEABLE)) return TICKET_CATEGORY.CHANGEABLE;
  if (markers.includes(TICKET_CATEGORY.UNCHANGEABLE)) return TICKET_CATEGORY.UNCHANGEABLE;
  return null;
};

const getChangeabilityBadgeStyle = (theme: AITTheme, kind: TicketCategory | null): React.CSSProperties => {
  if (kind === null) {
    return {
      fontSize: '12px',
      color: '#888888',
      fontWeight: 600 as const
    };
  }
  if (kind === TICKET_CATEGORY.CHANGEABLE) {
    return {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 700 as const,
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap' as const,
      backgroundColor: '#e8f5e9',
      color: '#1b5e20',
      border: `1px solid ${theme.linkColor}`,
      boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
    };
  }
  return {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 700 as const,
    letterSpacing: '0.02em',
    whiteSpace: 'nowrap' as const,
    backgroundColor: '#fff3e0',
    color: '#bf360c',
    border: '1px solid #ffb74d',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)'
  };
};

const changeabilityTooltip = (kind: TicketCategory | null): string | undefined => {
  if (kind === null) {
    return undefined;
  }
  if (kind === TICKET_CATEGORY.CHANGEABLE) {
    return '出発前に変更可能な運賃区分です。';
  }
  return '変更不可の運賃区分です（割引運賃など）。';
};

type Props = {
  item: TransportProposalItem;
  index: number;
  includeTax: boolean;
  onSelect: () => void;
  theme: AITTheme;
  disabled: boolean;
  hasRoundTripPair: boolean;
  bundleCategory: ProposalBundle['category'];
};

export const ForeignFlightLegCardRow = ({
  item,
  index,
  includeTax,
  onSelect,
  theme,
  disabled,
  hasRoundTripPair,
  bundleCategory
}: Props) => {
  const styles = useMemo(
    () => getStyles(theme, item.selected ?? false, disabled),
    [theme, item.selected, disabled]
  );
  const changeability = resolveChangeability(item);
  const badgeStyle = useMemo(() => getChangeabilityBadgeStyle(theme, changeability), [theme, changeability]);
  const circleNumber = getCircleNumber(index);
  const rowDisplay = buildForeignFlightRowDisplay(item, hasRoundTripPair);
  const priceFromApi = item.priceText?.trim() ?? '';
  const priceBase = priceFromApi || formatPriceWithMark(includeTax ? item.priceWithTax : item.price);
  const priceDisplay =
    priceBase != null && bundleCategory === PROPOSAL_BUNDLE_CATEGORY.OUTWARD && item.transport.isAir === true
      ? priceFromApi.length > 0
        ? priceBase
        : `${priceBase}（往復）`
      : priceBase ?? '-';

  const handleClick = () => {
    const selectable = (item.selected ?? false) === false && disabled === false;
    if (selectable) {
      onSelect();
    }
  };

  return (
    <tr style={styles.row}>
      <td style={styles.cellNarrow}>
        <div style={styles.numberCircle}>{circleNumber}</div>
      </td>
      <td style={styles.cellTime}>
        <TransportTimeRangeCell
          departureTime={item.transport.departureTime}
          arrivalTime={item.transport.arrivalTime}
          departureDate={item.transport.departureDate}
          arrivalDate={item.transport.arrivalDate}
        />
      </td>
      <td style={styles.cellMuted}>
        <div>{rowDisplay.durationMain ?? '—'}</div>
        {rowDisplay.durationSub ? <div style={styles.durationSub}>{rowDisplay.durationSub}</div> : null}
      </td>
      <td
        style={styles.cellSegment}
        title={rowDisplay.segmentLabel.length > SEGMENT_TITLE_MIN_LEN ? rowDisplay.segmentLabel : undefined}
      >
        <div style={{ minWidth: 0 }}>
          <TextTruncate lines={1}>{rowDisplay.segmentLabel}</TextTruncate>
        </div>
      </td>
      <td style={styles.cellAirline}>{rowDisplay.airlineCarrier.length > 0 ? rowDisplay.airlineCarrier : '—'}</td>
      <td style={styles.cell}>
        <div>
          <strong>{rowDisplay.flightMain}</strong>
        </div>
        {rowDisplay.ticketExtra ? <div style={styles.ticketSub}>{rowDisplay.ticketExtra}</div> : null}
      </td>
      <td style={styles.cell}>{rowDisplay.seatLabel}</td>
      <td style={styles.cellBadge}>
        <span style={badgeStyle} title={changeabilityTooltip(changeability)}>
          {changeability === null ? '—' : TICKET_CATEGORY_LABELS[changeability]}
        </span>
      </td>
      <td style={styles.cellPrice}>
        <div style={styles.priceContainer}>
          <span style={styles.priceText}>{priceDisplay}</span>
          <button
            type="button"
            style={styles.selectButton}
            onClick={handleClick}
            disabled={item.selected || disabled}
          >
            選択
          </button>
        </div>
      </td>
    </tr>
  );
};

const getStyles = (theme: AITTheme, selected: boolean, disabled: boolean) => ({
  row: {
    borderBottom: '1px solid #ececec',
    backgroundColor: selected ? '#e6f4fb' : 'transparent'
  },
  cellNarrow: {
    padding: '10px 4px',
    verticalAlign: 'middle' as const,
    width: '36px'
  },
  cell: {
    padding: '10px 6px',
    fontSize: '12px',
    verticalAlign: 'top' as const,
    lineHeight: 1.45
  },
  cellAirline: {
    padding: '10px 4px',
    fontSize: '11px',
    verticalAlign: 'top' as const,
    lineHeight: 1.45,
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
    minWidth: 0
  },
  cellTime: {
    padding: '10px 6px',
    fontSize: '12px',
    fontWeight: 600 as const,
    verticalAlign: 'top' as const,
    whiteSpace: 'nowrap' as const
  },
  cellMuted: {
    padding: '10px 6px',
    fontSize: '12px',
    color: '#555555',
    verticalAlign: 'top' as const,
    overflow: 'hidden' as const,
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
    minWidth: 0
  },
  durationSub: {
    marginTop: '4px',
    fontSize: '11px',
    color: '#666666',
    lineHeight: 1.35
  },
  ticketSub: {
    marginTop: '4px',
    fontSize: '10px',
    color: '#666666',
    fontWeight: 500 as const,
    lineHeight: 1.35
  },
  cellSegment: {
    padding: '10px 6px',
    fontSize: '11px',
    verticalAlign: 'top' as const,
    lineHeight: 1.45,
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
    overflow: 'hidden' as const,
    color: '#333333',
    minWidth: 0
  },
  cellBadge: {
    padding: '10px 2px 10px 4px',
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const
  },
  cellPrice: {
    padding: '10px 4px 10px 2px',
    textAlign: 'right' as const,
    verticalAlign: 'middle' as const,
    minWidth: 0
  },
  cellStack: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '2px'
  },
  priceContainer: {
    display: 'flex',
    flexDirection: 'row' as const,
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    justifyContent: 'flex-end',
    alignContent: 'flex-end',
    gap: '6px',
    fontWeight: 700 as const,
    color: theme.linkColor,
    fontSize: '13px',
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box' as const
  },
  priceText: {
    display: 'inline-block' as const,
    whiteSpace: 'nowrap' as const,
    textAlign: 'right' as const,
    lineHeight: 1.35,
    flex: '1 1 auto',
    minWidth: 0
  },
  numberCircle: {
    width: '26px',
    height: '26px',
    minWidth: '26px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '11px'
  },
  selectButton: {
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 600 as const,
    cursor: selected || disabled ? 'not-allowed' : 'pointer',
    whiteSpace: 'nowrap' as const,
    opacity: selected || disabled ? 0.5 : 1,
    flexShrink: 0
  }
});
